// cSpell:ignore afrekening bedrag betalen deze Gemiddeld januari juni maand maanden niet tellen telt veranderde vervallen waarvan
// The provision months a settlement counts, as the "i" after a provision
// line's calculation shows them (D7, Lewis 2026-10-16): a settlement counts
// every month of its period in which the lease ran. Since 2026-10-17 the
// tooltip is a short list of a label and its value, one row per fact that
// applies, instead of a block of sentences:
//
//   In deze afrekening   01/07/2026 t/m 31/12/2026 · 6 maanden
//   Nog te betalen       01/11/2026 t/m 31/12/2026 (telt al mee)
//   Gemiddeld            € 52,30 per maand: het bedrag veranderde, …
//
// It says the months by their dates, never by month names (Lewis,
// 2026-10-16: "instead of writing 'januari tot en met juni' in full
// everywhere, can you do this in dd/mm/yyyy?"); "jul–dec 2026, 6 maanden,
// waarvan 2 maanden nog niet vervallen" was not understood.

import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Interval, monthsBetween, subtractIntervals } from "./engine";

const parseDay = (iso: string) => {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return { year, month, day };
};

const lastDayOfMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

const pad = (n: number): string => String(n).padStart(2, "0");

// "01/07/2026", by the app's own date format. The day is built from its
// parts in local time, so no time zone moves it to the day before.
export const formatDay = (iso: string): string => {
  const { year, month, day } = parseDay(iso);
  return formatDate(new Date(year, month - 1, day));
};

// One label and its value in an "i" (InfoRows in the provisions step).
export interface IInfoRow {
  label: string;
  value: string;
}

// One interval by its first and last day ("01/07/2026 t/m 31/12/2026"), or
// its one day.
export const describeProvisionInterval = (interval: Interval): string => {
  const from = formatDay(interval.from);
  const to = formatDay(interval.to);
  if (from === to) return to;
  return getLocalizedText("settlement.provisions.period.range", { from, to });
};

// Several intervals, one after the other.
export const describeProvisionPeriods = (intervals: Interval[]): string =>
  intervals.map(describeProvisionInterval).join("; ");

export const monthsOf = (intervals: Interval[]): number =>
  Math.round(
    intervals.reduce((sum, iv) => sum + monthsBetween(iv.from, iv.to), 0) * 1e6,
  ) / 1e6;

// Months are shown with a decimal comma, as the rest of the step does.
export const formatMonthCount = (months: number): string =>
  Number.isInteger(months)
    ? String(months)
    : months.toFixed(2).replace(".", ",");

// "1 maand", "6 maanden".
export const monthCountText = (months: number): string =>
  months === 1
    ? getLocalizedText("settlement.provisions.month_count_one")
    : getLocalizedText("settlement.provisions.month_count_many", {
        months: formatMonthCount(months),
      });

// The counted days that are not due yet on `today` (yyyy-mm-dd): those of
// the months after today's. A provision is due at the start of its month, so
// today's own month is due.
const notYetDueIntervals = (params: {
  intervals: Interval[];
  today: string;
}): Interval[] => {
  const { year, month } = parseDay(params.today);
  const endOfThisMonth = `${year}-${pad(month)}-${pad(
    lastDayOfMonth(year, month),
  )}`;
  return subtractIntervals(params.intervals, [
    { from: "0000-01-01", to: endOfThisMonth },
  ]);
};

// The row of the months that count but are not due yet: "Nog te betalen
// 01/11/2026 t/m 31/12/2026 (telt al mee)", or "alle maanden (tellen al
// mee)" when none of them is due. Undefined when every counted month is
// due.
export const notYetDueRow = (params: {
  intervals: Interval[];
  today: string;
}): IInfoRow | undefined => {
  const rest = notYetDueIntervals(params);
  const notDue = monthsOf(rest);
  if (!(notDue > 0)) return undefined;
  return {
    label: getLocalizedText("settlement.provisions.info.not_due"),
    value:
      notDue === monthsOf(params.intervals)
        ? getLocalizedText("settlement.provisions.info.not_due_all")
        : getLocalizedText("settlement.provisions.info.not_due_value", {
            periods: describeProvisionPeriods(rest),
          }),
  };
};

// "01/07/2026 t/m 31/12/2026 · 6 maanden": days and their months.
export const countedValue = (intervals: Interval[]): string =>
  `${describeProvisionPeriods(intervals)} · ${monthCountText(
    monthsOf(intervals),
  )}`;

// The rows of the "i" after a calculation: the months it counts, the ones
// not due yet, and — when the amount is an average of months that held
// other amounts — that average (already formatted, "€ 52,30"). Empty when no
// month counts.
export const provisionMonthsRows = (params: {
  intervals: Interval[];
  today: string;
  averagePerMonth?: string;
}): IInfoRow[] => {
  const { intervals, today, averagePerMonth } = params;
  if (intervals.length === 0) return [];
  const notDue = notYetDueRow({ intervals, today });
  return [
    {
      label: getLocalizedText("settlement.provisions.info.counted"),
      value: countedValue(intervals),
    },
    ...(notDue ? [notDue] : []),
    ...(averagePerMonth
      ? [
          {
            label: getLocalizedText("settlement.provisions.info.average"),
            value: getLocalizedText(
              "settlement.provisions.info.average_value",
              { amount: averagePerMonth },
            ),
          },
        ]
      : []),
  ];
};
